// What the colours on this project's drawings MEAN.
//
// An area's colour is read off whatever the callout was painted, which
// was enough while there was one map: you could see that two zones were
// the same colour, but nothing said why. Across a re-issue it stops
// being enough - the same red on March's sheet and June's sheet is only
// the same statement if somebody wrote down what red means.
//
// Readable by anyone on the project; the owner edits it. Colours the
// drawings use that nobody has explained are offered as one-click rows,
// so the key gets filled in from the work rather than from a blank form.
import React, { useState } from "react";
import { Button, Input, Modal, Tag, Tooltip, message } from "antd";
import { BgColorsOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { saveAreaLegend } from "api/areas/areasApi";

const Swatch = ({ color, size = 13 }) => (
  <span
    style={{
      width: size, height: size, borderRadius: 3, display: "inline-block",
      background: color || "#cbd5e1", border: "1px solid rgba(0,0,0,.12)", flex: "0 0 auto",
    }}
  />
);

export default function AreaLegendStrip({ projectId, legend, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  if (!legend) return null;
  const entries = legend.entries || [];
  const unexplained = legend.unexplained || [];
  const canEdit = !!legend.canEdit;

  // Nothing to show and nothing to explain: stay out of the way.
  if (!entries.length && !unexplained.length) return null;

  const openEditor = () => {
    setRows([
      ...entries.map((e) => ({ ...e })),
      // The colours the drawings already use, ready to be named.
      ...unexplained.map((c) => ({ color: c, meaning: "", discipline: "", source: "manual" })),
    ]);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      // A row with no meaning is not a legend entry; dropping it here
      // keeps "unexplained" honest next time the page loads.
      const keep = rows.filter((r) => r.color && String(r.meaning || "").trim());
      const r = await saveAreaLegend(projectId, keep);
      message.success(`Legend saved — ${(r.entries || []).length} colour(s)`);
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      message.error(e.message || "Could not save the legend");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="d-flex align-items-center flex-wrap"
        style={{
          gap: 10, marginBottom: 12, padding: "7px 12px", fontSize: 13,
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
        }}
      >
        <BgColorsOutlined style={{ color: "#64748b" }} />
        <b style={{ color: "#475569" }}>Legend</b>
        {entries.map((e) => (
          <span key={e.color} className="d-inline-flex align-items-center" style={{ gap: 5 }}>
            <Swatch color={e.color} />
            <span>{e.meaning}</span>
            {e.discipline && <Tag style={{ marginInlineEnd: 0 }}>{e.discipline}</Tag>}
            {e.source === "ai" && (
              <Tooltip title="Read off the sheet's key block. Edit it and it becomes yours.">
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>from the sheet</Tag>
              </Tooltip>
            )}
          </span>
        ))}
        {!entries.length && (
          <span style={{ color: "#94a3b8" }}>
            No colour means anything yet — the same red on two drawings is a coincidence until it does.
          </span>
        )}
        {unexplained.length > 0 && (
          <Tooltip title={unexplained.join("  ")}>
            <span className="d-inline-flex align-items-center" style={{ gap: 4, color: "#b45309" }}>
              {unexplained.slice(0, 6).map((c) => <Swatch key={c} color={c} size={11} />)}
              <span>{unexplained.length} unexplained</span>
            </span>
          </Tooltip>
        )}
        {canEdit && (
          <Button size="small" onClick={openEditor} style={{ marginLeft: "auto" }}>
            Edit legend
          </Button>
        )}
      </div>

      <Modal
        open={open}
        title="What the colours mean"
        onCancel={() => setOpen(false)}
        onOk={save}
        okText="Save legend"
        confirmLoading={busy}
        width={640}
      >
        <div style={{ color: "#64748b", fontSize: 13, marginBottom: 12 }}>
          The drawing decides what colour a zone is; this decides what that colour says.
          A row left without a meaning is not saved.
        </div>
        {rows.map((r, i) => (
          <div key={`${r.color}-${i}`} className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8 }}>
            <input
              type="color"
              value={/^#[0-9a-f]{6}$/i.test(r.color || "") ? r.color : "#3b82f6"}
              onChange={(e) => setRows((v) => v.map((x, j) => (j === i ? { ...x, color: e.target.value } : x)))}
              style={{ width: 34, height: 30, padding: 0, border: "1px solid #d9d9d9", borderRadius: 4 }}
            />
            <Input
              placeholder="What it means, e.g. “Hydronic mains — due Q2”"
              value={r.meaning}
              onChange={(e) => setRows((v) => v.map((x, j) => (j === i ? { ...x, meaning: e.target.value } : x)))}
            />
            <Input
              placeholder="Discipline"
              style={{ maxWidth: 150 }}
              value={r.discipline}
              onChange={(e) => setRows((v) => v.map((x, j) => (j === i ? { ...x, discipline: e.target.value } : x)))}
            />
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setRows((v) => v.filter((x, j) => j !== i))}
            />
          </div>
        ))}
        <Button
          icon={<PlusOutlined />}
          onClick={() => setRows((v) => [...v, { color: "#3b82f6", meaning: "", discipline: "", source: "manual" }])}
        >
          Add a colour
        </Button>
      </Modal>
    </>
  );
}
